import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Clock,
  Contact,
  Filter,
  Mail,
  Phone,
} from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { fetchDirectoryGroups, type DirectoryContact } from './api';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Directory(): JSX.Element {
  const copy = pageCopy().directory;
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const query = useQuery({ queryKey: ['directory'], queryFn: fetchDirectoryGroups });
  const groups = query.data ?? [];
  const visibleGroups = useMemo(
    () => selectedGroupIds.length === 0
      ? groups
      : groups.filter((group) => selectedGroupIds.includes(group.id)),
    [groups, selectedGroupIds],
  );
  const visibleContacts = useMemo(
    () => visibleGroups.flatMap((group) => (
      group.contacts.map((contact) => ({
        contact,
        categoryName: group.name,
      }))
    )),
    [visibleGroups],
  );

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      {groups.length > 0 ? (
        <CategoryFilterDropdown
          groups={groups.map((group) => ({ id: group.id, name: group.name }))}
          open={filtersOpen}
          selectedGroupIds={selectedGroupIds}
          onOpenChange={setFiltersOpen}
          onSelectedGroupIdsChange={setSelectedGroupIds}
        />
      ) : null}

      <div className="flex flex-col gap-3 px-5 pt-4 pb-safe-or-6">
        {query.isLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : visibleContacts.length === 0 ? (
          <EmptyState
            icon={<Contact className="h-7 w-7" strokeWidth={1.5} />}
            title="No contacts."
            description="Your estate hasn't published a directory yet."
          />
        ) : (
          visibleContacts.map(({ contact, categoryName }) => (
            <ContactCard
              key={`${categoryName}-${contact.id}`}
              contact={contact}
              categoryName={categoryName}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface CategoryFilterDropdownProps {
  groups: Array<{ id: string; name: string }>;
  open: boolean;
  selectedGroupIds: string[];
  onOpenChange: (open: boolean) => void;
  onSelectedGroupIdsChange: (ids: string[]) => void;
}

function CategoryFilterDropdown({
  groups,
  open,
  selectedGroupIds,
  onOpenChange,
  onSelectedGroupIdsChange,
}: CategoryFilterDropdownProps): JSX.Element {
  const selectedCount = selectedGroupIds.length;
  const label = selectedCount === 0 ? 'All categories' : `${selectedCount} selected`;

  function toggleGroup(groupId: string): void {
    onSelectedGroupIdsChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter((id) => id !== groupId)
        : [...selectedGroupIds, groupId],
    );
  }

  return (
    <div className="relative px-5 pt-4">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center gap-2 rounded-2xl bg-card px-4 py-3 text-left text-body-sm font-semibold text-ink shadow-card hairline"
        aria-expanded={open}
      >
        <Filter className="h-4 w-4 shrink-0 text-primary-700" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate">Categories</span>
        <span className="shrink-0 text-body-xs font-medium text-ink-secondary">{label}</span>
        <ChevronDown
          className={'h-4 w-4 shrink-0 text-ink-muted transition-transform ' + (open ? 'rotate-180' : '')}
          strokeWidth={1.75}
        />
      </button>

      {open ? (
        <div className="absolute left-5 right-5 top-full z-20 mt-2 max-h-72 overflow-y-auto rounded-2xl bg-card p-2 shadow-elevated hairline">
          <button
            type="button"
            onClick={() => onSelectedGroupIdsChange([])}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-body-sm font-medium text-ink"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
              {selectedCount === 0 ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : null}
            </span>
            All contact groups
          </button>

          {groups.map((group) => {
            const checked = selectedGroupIds.includes(group.id);
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-body-sm text-ink"
              >
                <span
                  className={
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-pill ' +
                    (checked ? 'bg-primary-700 text-white' : 'bg-paper hairline')
                  }
                >
                  {checked ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{group.name}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ContactCard({
  contact,
  categoryName,
}: {
  contact: DirectoryContact;
  categoryName: string;
}): JSX.Element {
  return (
    <Card asChild noGrain className="rounded-xl p-4">
      <article>
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-primary-50">
            {contact.profile_image_url ? (
              <img src={contact.profile_image_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-body-sm font-semibold text-primary-700">
                {initials(contact.name)}
              </span>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-headline leading-tight text-ink">{contact.name}</p>
            {contact.role ? (
              <p className="text-body-sm text-ink-secondary">{contact.role}</p>
            ) : null}
            <span className="mt-1 inline-flex max-w-full items-center rounded-pill bg-primary-50 px-2 py-0.5 text-[11px] font-semibold leading-4 text-primary-700">
              <span className="truncate">{categoryName}</span>
            </span>
          </div>
          {contact.mobile ? (
            <a
              href={`tel:${contact.mobile}`}
              aria-label={`Call ${contact.name}`}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-700 text-white"
            >
              <Phone className="h-4.5 w-4.5" strokeWidth={1.75} />
            </a>
          ) : null}
        </div>

        <div className="mt-3 flex flex-col gap-1.5">
          {contact.mobile ? (
            <a
              href={`tel:${contact.mobile}`}
              className="flex items-center gap-2 text-body-sm font-semibold text-primary-700"
            >
              <Phone className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {contact.mobile}
            </a>
          ) : null}
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-body-sm text-ink-secondary"
            >
              <Mail className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
              <span className="truncate">{contact.email}</span>
            </a>
          ) : null}
          {contact.hours ? (
            <p className="flex items-center gap-2 text-body-sm text-ink-muted">
              <Clock className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              {contact.hours}
            </p>
          ) : null}
        </div>
      </article>
    </Card>
  );
}
